import React, { useState, useEffect, useRef, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import supabase from './supabase/supabase';
import DoorControlGrid from './components/DoorControlGrid';
import DisplayTargets from './components/DisplayTargets';
import SettingsPanel from './components/SettingsPanel';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import MainDisplay from './pages/MainDisplay';
import LobbyDisplay from './pages/LobbyDisplay';
import NoticeDisplay from './pages/NoticeDisplay';
import Login from './pages/Login';
import UpdatePassword from './pages/UpdatePassword';
import Welcome from './pages/Welcome';

const { FiSettings, FiCalendar, FiCheckCircle, FiAlertTriangle } = FiIcons;

// ─── CRITICAL: Capture OAuth code at module evaluation time ──────────────────
(function captureOAuthCode() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  if (code) {
    sessionStorage.setItem('oauth_pending_code', code);
    window.history.replaceState(null, '', window.location.pathname);
  }
})();

// ─── Utility helpers ──────────────────────────────────────────────────────────

const getTzDay = (date, tz) => {
  try {
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: tz }));
    return tzDate.getDay();
  } catch (e) { return date.getDay(); }
};

const getTzDateStr = (date, tz) => {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric'
    }).format(date);
  } catch (e) { return date.toDateString(); }
};

const getLocalTimeStr = (date, tz) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      hour: '2-digit', minute: '2-digit', timeZone: tz, hourCycle: 'h23'
    }).formatToParts(date);
    const h = parts.find(p => p.type === 'hour')?.value || '00';
    const m = parts.find(p => p.type === 'minute')?.value || '00';
    return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
  } catch (e) {
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  }
};

// ─── Parse Supabase hash params safely ───────────────────────────────────────
// Only strips the leading '#' so token values containing '#' are never corrupted.
const parseHashParams = (hash) => new URLSearchParams(hash.replace(/^#/, '?'));

// ─── Email Updated Toast ──────────────────────────────────────────────────────

function EmailUpdatedToast({ onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 6000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[200] flex items-center gap-3 bg-zinc-900 border border-emerald-500/30 rounded-2xl px-5 py-4 shadow-2xl shadow-black/60">
      <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
        <SafeIcon icon={FiCheckCircle} className="text-emerald-400 text-base" />
      </div>
      <div className="flex flex-col">
        <span className="text-sm font-semibold text-white tracking-tight">Email address updated.</span>
        <span className="text-xs text-zinc-400">Your new email is now active on your account.</span>
      </div>
      <button onClick={onDismiss} className="ml-2 text-zinc-600 hover:text-zinc-300 transition-colors text-lg leading-none">×</button>
    </div>
  );
}

// ─── Auth Warning Banner ──────────────────────────────────────────────────────

function CalendarWarningBanner({ onReAuth }) {
  return (
    <button
      onClick={onReAuth}
      className="w-full flex items-center justify-center gap-2.5 px-4 py-3 bg-amber-500/10 border-b border-amber-500/20 hover:bg-amber-500/15 transition-colors group"
    >
      <SafeIcon icon={FiAlertTriangle} className="text-amber-400 text-sm flex-shrink-0" />
      <span className="text-xs font-semibold text-amber-300 tracking-tight">
        Google Calendar Sync Paused.{' '}
        <span className="underline underline-offset-2 group-hover:text-amber-200 transition-colors">
          Click here to re-authenticate.
        </span>
      </span>
      <SafeIcon icon={FiAlertTriangle} className="text-amber-400 text-sm flex-shrink-0" />
    </button>
  );
}

// ─── Sign Preview ─────────────────────────────────────────────────────────────

const CALENDAR_DRIVEN_STATES = new Set(['RECORDING', 'WRAPUP']);

function getAutoPilotBadgeLabel(displayState) {
  if (CALENDAR_DRIVEN_STATES.has(displayState)) return 'Following Calendar';
  return 'Following Schedule';
}

function SignPreview({ displayState, isManualOverride, dynamicTargetTime, nextSessionText }) {
  const displayLabels = {
    OPEN: 'STUDIO OPEN', RECORDING: 'RECORDING', LEADIN: 'STANDBY',
    WRAPUP: 'WRAPPING UP', BREAK: 'ON BREAK', CLOSED: 'STUDIO CLOSED'
  };

  const getGradient = (s) => ({
    CLOSED: 'from-zinc-500', RECORDING: 'from-[#E11D48]', LEADIN: 'from-[#F59E0B]',
    WRAPUP: 'from-slate-500', BREAK: 'from-violet-500', OPEN: 'from-[#10B981]'
  }[s] || 'from-zinc-500');

  const getTextColor = (s) => ({
    CLOSED: 'text-zinc-400',
    RECORDING: 'text-[#E11D48] drop-shadow-[0_0_15px_rgba(225,29,72,0.3)]',
    LEADIN: 'text-[#F59E0B] drop-shadow-[0_0_15px_rgba(245,158,11,0.3)]',
    WRAPUP: 'text-slate-400 drop-shadow-[0_0_15px_rgba(148,163,184,0.3)]',
    BREAK: 'text-violet-400 drop-shadow-[0_0_15px_rgba(139,92,246,0.3)]',
    OPEN: 'text-[#10B981] drop-shadow-[0_0_15px_rgba(16,185,129,0.3)]'
  }[s] || 'text-zinc-400');

  let subtext = '';
  if (isManualOverride) {
    if (displayState === 'CLOSED') subtext = 'Studio locked manually';
    else if (displayState === 'RECORDING') subtext = 'Recording mode forced';
    else if (displayState === 'BREAK') subtext = `Be back at ${dynamicTargetTime || 'soon'}`;
    else if (displayState === 'LEADIN') subtext = `Next recording starts at ${dynamicTargetTime || 'soon'}`;
    else if (displayState === 'WRAPUP') subtext = `Available at ${dynamicTargetTime || 'soon'}`;
    else if (displayState === 'OPEN') subtext = 'Studio held open';
  } else {
    if (displayState === 'OPEN') subtext = nextSessionText || '';
    else if (displayState === 'RECORDING') subtext = 'Do Not Disturb';
    else if (displayState === 'BREAK') subtext = `Be back at ${dynamicTargetTime || 'soon'}`;
    else if (displayState === 'LEADIN') subtext = `Next recording starts at ${dynamicTargetTime || 'soon'}`;
    else if (displayState === 'WRAPUP') subtext = `Available at ${dynamicTargetTime || 'soon'}`;
  }

  const badgeLabel = isManualOverride ? 'Manual Override Active' : getAutoPilotBadgeLabel(displayState);
  const badgeStyle = isManualOverride
    ? 'bg-amber-500/10 text-amber-500 border-amber-500/20'
    : CALENDAR_DRIVEN_STATES.has(displayState)
      ? 'bg-violet-500/10 text-violet-400 border-violet-500/20'
      : 'bg-blue-500/10 text-blue-400 border-blue-500/20';

  return (
    <div className="flex-shrink-0 flex flex-col items-center justify-center py-8 rounded-2xl bg-zinc-900/40 border border-white/10 relative overflow-hidden w-full shadow-lg">
      <div className={`absolute inset-0 bg-gradient-to-b opacity-20 pointer-events-none transition-colors duration-500 ${getGradient(displayState)}`} />
      <div className="text-center relative z-10 w-full px-4">
        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold tracking-widest uppercase mb-4 border transition-colors duration-500 ${badgeStyle}`}>
          {badgeLabel}
        </div>
        <div className={`text-3xl md:text-4xl font-extrabold tracking-tight mb-2 transition-colors duration-500 ${getTextColor(displayState)}`}>
          {displayLabels[displayState] || displayState}
        </div>
        <div className="text-sm md:text-base text-zinc-400 font-medium h-6">{subtext}</div>
      </div>
    </div>
  );
}

// ─── Admin Dashboard ──────────────────────────────────────────────────────────

function AdminDashboard() {
  const [isBooting, setIsBooting] = useState(true);
  const [timeModalState, setTimeModalState] = useState(null);
  const [customTime, setCustomTime] = useState('');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [authFailed, setAuthFailed] = useState(false);
  const [forceSyncTick, setForceSyncTick] = useState(Date.now());
  const [showEmailUpdatedToast, setShowEmailUpdatedToast] = useState(false);
  const [userId, setUserId] = useState(null);

  const oauthCodeRef = useRef(sessionStorage.getItem('oauth_pending_code') || null);
  const settingsLoadedRef = useRef(false);
  const sessionTokenRef = useRef(null);

  const [settings, setSettings] = useState({
    active_door_state: 'AUTO', dynamic_time_target: '', target_time_ms: null,
    studio_name: '', show_name: true, show_clock: true,
    left_card_type: 'Wi-Fi', left_card_input_1: '', left_card_input_2: '',
    right_card_type: 'QR Code', right_card_input_1: '', right_card_input_2: '',
    notice_header: '', notice_subtext: '', operating_days: [1, 2, 3, 4, 5],
    timezone: null, open_time: '09:00', close_time: '17:00',
    lead_in_buffer: 15, wrap_up_buffer: 5, lobby_welcome: '', lobby_subtext: '',
    is_calendar_connected: false, google_connected_email: '', tracked_keywords: []
  });

  useEffect(() => { document.title = "StudioSign | Admin Dashboard"; }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'USER_UPDATED') setShowEmailUpdatedToast(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    let lastSync = Date.now();
    const triggerWakeUpSync = () => {
      const now = Date.now();
      if (now - lastSync > 1000) {
        lastSync = now;
        setCurrentTime(new Date());
        setForceSyncTick(now);
      }
    };
    const handleVisibilityChange = () => { if (document.visibilityState === 'visible') triggerWakeUpSync(); };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', triggerWakeUpSync);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', triggerWakeUpSync);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => { if (timeModalState) setCustomTime(''); }, [timeModalState]);

  // ── Initial settings fetch ────────────────────────────────────────────────
  useEffect(() => {
    let isMounted = true;
    const fetchSettings = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        if (isMounted) { settingsLoadedRef.current = true; setIsBooting(false); }
        return;
      }

      sessionTokenRef.current = session.access_token;
      if (isMounted) setUserId(session.user.id);

      let { data, error } = await supabase
        .from('studio_settings')
        .select('id, active_door_state, dynamic_time_target, target_time_ms, studio_name, show_name, show_clock, left_card_type, left_card_input_1, left_card_input_2, right_card_type, right_card_input_1, right_card_input_2, notice_header, notice_subtext, operating_days, timezone, open_time, close_time, lead_in_buffer, wrap_up_buffer, lobby_welcome, lobby_subtext, is_calendar_connected, google_connected_email, tracked_keywords')
        .eq('id', session.user.id)
        .single();

      if (error && error.code === 'PGRST116') {
        const { data: newData, error: insertError } = await supabase
          .from('studio_settings')
          .insert([{ id: session.user.id }])
          .select().single();
        if (!insertError) { data = newData; error = null; }
      }

      if (data && !error && isMounted) {
        let updates = {};
        if (!data.timezone) {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          updates.timezone = tz;
          supabase.from('studio_settings').update({ timezone: tz }).eq('id', session.user.id).then();
        }
        if (!data.active_door_state) updates.active_door_state = 'AUTO';
        setSettings(prev => ({
          ...prev, ...data, ...updates,
          active_door_state: updates.active_door_state || (data.active_door_state ? data.active_door_state.toUpperCase() : 'AUTO')
        }));
      }

      if (isMounted) {
        settingsLoadedRef.current = true;
        setIsBooting(false);
      }
    };
    fetchSettings();
    return () => { isMounted = false; };
  }, []);

  // ── OAuth processor ───────────────────────────────────────────────────────
  useEffect(() => {
    const code = oauthCodeRef.current;
    if (!code || !userId || !settingsLoadedRef.current) return;

    oauthCodeRef.current = null;
    sessionStorage.removeItem('oauth_pending_code');
    setAuthFailed(false);

    const processOAuthCode = async () => {
      try {
        console.log('[OAuth] Starting token exchange...');

        const { data, error: fnError } = await supabase.functions.invoke('google-token-exchange', {
          body: {
            code,
            grant_type: 'authorization_code',
            redirect_uri: window.location.origin + '/admin'
          }
        });

        if (fnError) {
          console.error('[OAuth] Edge Function failed:', fnError.message, fnError);
          return;
        }

        console.log('[OAuth] Raw Edge Function response:', JSON.stringify(data));

        const payload = (data?.access_token || data?.refresh_token)
          ? data
          : (data?.data ?? {});

        const access_token = payload.access_token || payload.accessToken || null;
        const refresh_token = payload.refresh_token || payload.refreshToken || null;

        if (!access_token) {
          console.error('[OAuth] No access_token found. Full response:', JSON.stringify(data));
          return;
        }

        console.log('[OAuth] Token exchange successful. Fetching email...');
        let email = '';
        try {
          const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
            headers: { Authorization: `Bearer ${access_token}` }
          });
          if (res.ok) {
            const info = await res.json();
            email = info.email || '';
            console.log('[OAuth] Google account:', email);
          } else {
            console.warn('[OAuth] userinfo status:', res.status);
          }
        } catch (e) {
          console.error('[OAuth] userinfo fetch failed:', e);
        }

        const updates = {
          google_access_token: access_token,
          is_calendar_connected: true,
          google_connected_email: email
        };
        if (refresh_token) updates.google_refresh_token = refresh_token;

        console.log('[OAuth] Writing to DB (authenticated) for userId:', userId);

        // Fix R-FC93C: credentials sourced exclusively from env — no hardcoded fallback
        const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
        const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

        if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
          console.error('[OAuth] CRITICAL — Supabase env vars missing. Cannot write tokens.');
          return;
        }

        const { createClient } = await import('@supabase/supabase-js');
        const authedClient = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
          global: {
            headers: { Authorization: `Bearer ${sessionTokenRef.current}` }
          }
        });

        const { error: dbError, count } = await authedClient
          .from('studio_settings')
          .update(updates)
          .eq('id', userId)
          .select('id', { count: 'exact', head: true });

        if (dbError) {
          console.error('[OAuth] CRITICAL — DB write failed:', dbError.message, dbError);
        } else {
          console.log(`[OAuth] ✅ Calendar connected and persisted. Rows updated: ${count}`);
          setSettings(prev => ({ ...prev, is_calendar_connected: true, google_connected_email: email }));
          setForceSyncTick(Date.now());
        }

      } catch (err) {
        console.error('[OAuth] Unexpected exception:', err);
      }
    };

    processOAuthCode();
  }, [userId, isBooting]);

  // ── Calendar polling ──────────────────────────────────────────────────────
  const calendarErrorCountRef = useRef(0);

  useEffect(() => {
    if (!settings?.is_calendar_connected || !userId) { setEvents([]); return; }
    if (authFailed) return;

    const fetchEvents = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-calendar-events', {
          body: { studio_id: userId }
        });
        if (error) {
          calendarErrorCountRef.current += 1;
          console.error(`[Calendar] Error (attempt ${calendarErrorCountRef.current}):`, error.message);
          if (calendarErrorCountRef.current >= 3) { setAuthFailed(true); setEvents([]); }
          return;
        }
        calendarErrorCountRef.current = 0;
        const rawEvents = data?.events || [];
        let keywords = Array.isArray(settings.tracked_keywords)
          ? settings.tracked_keywords
          : (typeof settings.tracked_keywords === 'string' ? settings.tracked_keywords.split(',') : []);
        keywords = keywords.map(k => String(k).toLowerCase().trim()).filter(Boolean);
        const filtered = rawEvents.filter(item => {
          if (item.status === 'cancelled') return false;
          if (!item.start?.dateTime || !item.end?.dateTime) return false;
          if (keywords.length === 0) return true;
          return keywords.some(kw => String(item.summary || '').toLowerCase().includes(kw));
        });
        setEvents(filtered.map(e => ({ start: e.start.dateTime, end: e.end.dateTime, summary: e.summary })));
      } catch (e) { console.error('[Calendar] Exception:', e); }
    };

    fetchEvents();
    const interval = setInterval(fetchEvents, 30000);
    return () => clearInterval(interval);
  }, [settings?.is_calendar_connected, settings?.tracked_keywords, userId, authFailed, forceSyncTick]);

  // ── Manual override expiry watchdog ──────────────────────────────────────
  useEffect(() => {
    if (!userId) return;
    const id = setInterval(() => {
      setSettings(cur => {
        if (cur.target_time_ms && Date.now() >= cur.target_time_ms) {
          supabase.from('studio_settings')
            .update({ active_door_state: 'AUTO', dynamic_time_target: '', target_time_ms: null })
            .eq('id', userId).then();
          return { ...cur, active_door_state: 'AUTO', dynamic_time_target: '', target_time_ms: null };
        }
        return cur;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [userId]);

  // ── Auto-Pilot schedule brain ─────────────────────────────────────────────
  const autoComputed = useMemo(() => {
    if (!settings) return { state: 'CLOSED', targetTime: null };
    try {
      const tz = settings.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      const currentDayInt = getTzDay(currentTime, tz);
      const activeDays = (settings.operating_days || [1, 2, 3, 4, 5]).map(Number);
      let baseState = 'CLOSED';
      if (activeDays.includes(currentDayInt)) {
        const currentTimeStr = getLocalTimeStr(currentTime, tz);
        const openTime = settings.open_time || '09:00';
        const closeTime = settings.close_time || '17:00';
        const inHours = openTime > closeTime
          ? (currentTimeStr >= openTime || currentTimeStr < closeTime)
          : (currentTimeStr >= openTime && currentTimeStr < closeTime);
        if (inHours) baseState = 'OPEN';
      }
      const currentMs = currentTime.getTime();
      const leadInMs = (isNaN(parseInt(settings.lead_in_buffer, 10)) ? 15 : parseInt(settings.lead_in_buffer, 10)) * 60000;
      const wrapUpMs = (isNaN(parseInt(settings.wrap_up_buffer, 10)) ? 5 : parseInt(settings.wrap_up_buffer, 10)) * 60000;
      let calendarState = null, targetTime = null;
      for (const event of events) {
        if (!event.start || !event.end) continue;
        try {
          const startMs = new Date(event.start).getTime();
          const endMs = new Date(event.end).getTime();
          if (isNaN(startMs) || isNaN(endMs)) continue;
          if (currentMs >= startMs && currentMs < endMs) { calendarState = 'RECORDING'; targetTime = null; break; }
          else if (currentMs >= (startMs - leadInMs) && currentMs < startMs) {
            if (calendarState !== 'RECORDING') { calendarState = 'LEADIN'; targetTime = new Date(startMs).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz }); }
          } else if (currentMs >= endMs && currentMs < (endMs + wrapUpMs)) {
            if (calendarState !== 'RECORDING' && calendarState !== 'LEADIN') { calendarState = 'WRAPUP'; targetTime = new Date(endMs + wrapUpMs).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz }); }
          }
        } catch (err) { continue; }
      }
      return calendarState ? { state: calendarState, targetTime } : { state: baseState, targetTime: null };
    } catch (e) { console.error('Auto Brain Error:', e); return { state: 'CLOSED', targetTime: null }; }
  }, [settings, currentTime, events]);

  const nextSessionText = useMemo(() => {
    if (!events || events.length === 0) return null;
    const currentMs = currentTime.getTime();
    try {
      const tz = settings?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      const upcoming = events.filter(e => { const t = new Date(e.start).getTime(); return !isNaN(t) && t > currentMs; });
      if (upcoming.length > 0) {
        const next = upcoming[0];
        const nextStart = new Date(next.start);
        if (!isNaN(nextStart.getTime()) && getTzDateStr(nextStart, tz) === getTzDateStr(currentTime, tz)) {
          return `Next session at ${nextStart.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz })}`;
        }
      }
    } catch (err) { console.warn('Error calculating next session text:', err); }
    return null;
  }, [events, currentTime, settings?.timezone]);

  const manualState = settings.active_door_state?.toUpperCase() || 'AUTO';
  const isManualOverride = manualState !== 'AUTO' && manualState !== 'NONE';
  const finalState = isManualOverride ? manualState : autoComputed.state;
  const dynamicTargetTime = isManualOverride ? (settings.dynamic_time_target || null) : autoComputed.targetTime;

  const updateSetting = async (key, value) => {
    const finalValue = (key === 'active_door_state' && typeof value === 'string') ? value.toUpperCase() : value;
    const updates = { [key]: finalValue };
    if (key === 'active_door_state') { updates.dynamic_time_target = ''; updates.target_time_ms = null; }
    setSettings(prev => ({ ...prev, ...updates }));
    if (userId) { const { error } = await supabase.from('studio_settings').update(updates).eq('id', userId); return !error; }
    return false;
  };

  const updateMultipleSettings = async (updatesObj) => {
    setSettings(prev => ({ ...prev, ...updatesObj }));
    if (userId) { const { error } = await supabase.from('studio_settings').update(updatesObj).eq('id', userId); return !error; }
    return false;
  };

  const handleTimeSubmit = async (minutes) => {
    const parsedMinutes = parseInt(minutes, 10) || 0;
    const targetMs = Date.now() + parsedMinutes * 60000;
    const tz = settings.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const formattedTime = new Date(targetMs).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz });
    const newState = timeModalState.toUpperCase();
    const updates = { active_door_state: newState, dynamic_time_target: formattedTime, target_time_ms: targetMs };
    setSettings(prev => ({ ...prev, ...updates }));
    if (userId) await supabase.from('studio_settings').update(updates).eq('id', userId);
    setTimeModalState(null);
  };

  const getModalTitle = () => ({ break: 'Break', leadin: 'Lead In', wrapup: 'Wrap Up' }[timeModalState] || '');

  const handleConnectCalendar = () => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) { alert('Google Client ID is missing. Please configure VITE_GOOGLE_CLIENT_ID in your .env file.'); return; }
    setAuthFailed(false);
    calendarErrorCountRef.current = 0;
    const redirectUri = window.location.origin + '/admin';
    const scope = 'https://www.googleapis.com/auth/calendar.readonly https://www.googleapis.com/auth/userinfo.email';
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&access_type=offline&prompt=consent&scope=${encodeURIComponent(scope)}`;
  };

  if (isBooting) {
    return (
      <div className="h-screen w-screen bg-[#09090B] flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-zinc-900 border-t-zinc-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-screen w-screen overflow-y-auto overflow-x-hidden no-scrollbar bg-[#09090B] text-zinc-100 flex flex-col items-center selection:bg-zinc-800 relative">
      {authFailed && <div className="w-full flex-shrink-0"><CalendarWarningBanner onReAuth={handleConnectCalendar} /></div>}
      {showEmailUpdatedToast && <EmailUpdatedToast onDismiss={() => setShowEmailUpdatedToast(false)} />}

      <div className="w-full max-w-[800px] min-h-full flex flex-col px-6 py-6 md:py-12">
        <div className="flex-1 flex flex-col justify-center py-4">

          <div className="relative mb-10 flex items-center justify-center flex-shrink-0">
            <h1 className="text-3xl md:text-4xl tracking-tight">
              <span className="font-medium text-zinc-400">Studio</span>
              <span className="font-extrabold text-white">Sign</span>
            </h1>
            <button onClick={() => setIsSettingsOpen(true)}
              className="absolute right-0 text-zinc-500 hover:text-white transition-colors p-2 rounded-full hover:bg-zinc-800/50">
              <SafeIcon icon={FiSettings} className="text-xl" />
            </button>
          </div>

          <div className="flex-shrink-0 mb-8">
            <SignPreview
              displayState={finalState} isManualOverride={isManualOverride}
              dynamicTargetTime={dynamicTargetTime} nextSessionText={nextSessionText}
            />
          </div>

          {!settings.is_calendar_connected && !authFailed && (
            <div className="flex-shrink-0 mb-8 flex flex-col items-center justify-center py-6 rounded-2xl bg-zinc-900/40 border border-amber-500/20 text-center px-6">
              <SafeIcon icon={FiCalendar} className="text-3xl text-amber-500 mb-3" />
              <h3 className="text-lg font-bold text-white mb-2">Schedule Offline</h3>
              <p className="text-sm text-zinc-400 max-w-sm mb-4 leading-relaxed">
                Connect your Google Calendar so the schedule engine can track your bookings.
              </p>
              <button onClick={handleConnectCalendar}
                className="py-2.5 px-6 rounded-xl font-bold tracking-tight text-black bg-white hover:bg-zinc-200 transition-colors text-sm">
                CONNECT CALENDAR
              </button>
            </div>
          )}

          <div className="flex-shrink-0 mb-10">
            <DoorControlGrid
              activeState={isManualOverride ? settings.active_door_state?.toLowerCase() : null}
              setActiveState={(val) => updateSetting('active_door_state', val)}
              onTimeActionClick={(stateId) => setTimeModalState(stateId)}
            />
            {settings.target_time_ms && isManualOverride && (
              <div className="text-xs text-zinc-500 font-medium mt-4 text-center bg-zinc-900/40 py-2.5 rounded-lg border border-white/5">
                {settings.active_door_state?.toLowerCase() === 'break' && `Auto-resuming schedule at ${settings.dynamic_time_target}`}
                {settings.active_door_state?.toLowerCase() === 'wrapup' && `Auto-resuming schedule at ${settings.dynamic_time_target}`}
                {settings.active_door_state?.toLowerCase() === 'leadin' && `Auto-resuming schedule at ${settings.dynamic_time_target}`}
              </div>
            )}
          </div>

          <div className="flex-shrink-0">
            <DisplayTargets settings={settings} updateSetting={updateSetting} />
          </div>

        </div>
      </div>

      {timeModalState && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md px-6">
          <div className="w-full max-w-[320px] bg-[#09090B] border border-white/10 rounded-3xl p-6 shadow-2xl flex flex-col gap-6">
            <h3 className="text-lg font-bold text-white text-center tracking-tight uppercase">
              Set {getModalTitle()} Duration
            </h3>
            <div className="flex flex-col gap-3">
              {(timeModalState === 'break' ? [5, 15, 30] : [15, 30, 60]).map(time => (
                <button key={time} onClick={() => handleTimeSubmit(time)}
                  className="py-3.5 rounded-xl bg-zinc-900 border border-white/5 hover:bg-zinc-800 hover:border-white/10 text-white font-semibold tracking-tight transition-all active:scale-[0.98]">
                  {time === 60 ? '1 Hour' : `${time} Min`}
                </button>
              ))}
              <div className="relative flex items-center">
                <input type="number" placeholder="Custom (Min)" value={customTime}
                  onChange={(e) => setCustomTime(e.target.value)}
                  className="w-full py-3.5 pl-4 pr-16 rounded-xl bg-zinc-900 border border-white/5 focus:bg-zinc-800 focus:border-white/20 text-white font-semibold tracking-tight transition-all outline-none placeholder:text-zinc-600 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <button onClick={() => { if (customTime) handleTimeSubmit(Number(customTime)); }}
                  className={`absolute right-2 top-2 bottom-2 px-4 rounded-lg font-bold text-xs tracking-tight transition-all flex items-center justify-center ${
                    customTime ? 'bg-zinc-200 text-zinc-900 hover:bg-white shadow-sm active:scale-[0.96]' : 'bg-transparent text-zinc-600 pointer-events-none'
                  }`}>
                  SET
                </button>
              </div>
            </div>
            <button onClick={() => setTimeModalState(null)}
              className="py-3 text-zinc-500 hover:text-zinc-300 font-medium transition-colors tracking-tight text-sm uppercase">
              Cancel
            </button>
          </div>
        </div>
      )}

      <SettingsPanel
        isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)}
        settings={settings} updateSetting={updateSetting}
        updateMultipleSettings={updateMultipleSettings} userId={userId}
        onConnectCalendar={handleConnectCalendar}
      />
    </div>
  );
}

// ─── Protected Route ──────────────────────────────────────────────────────────

function ProtectedRoute({ children }) {
  const [status, setStatus] = useState('loading');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!isMounted) return;
      const hashParams = parseHashParams(window.location.hash);
      if (hashParams.get('type') === 'recovery') { navigate('/update-password', { replace: true }); return; }
      setStatus(session ? 'authed' : 'unauthed');
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;
      if (event === 'PASSWORD_RECOVERY') { navigate('/update-password', { replace: true }); return; }
      if (event === 'SIGNED_OUT') { setStatus('unauthed'); return; }
    });

    return () => { isMounted = false; subscription.unsubscribe(); };
  }, [navigate]);

  if (status === 'loading') {
    return (
      <div className="flex h-screen items-center justify-center bg-[#111111] text-zinc-400 font-medium tracking-tight">
        Loading StudioSign...
      </div>
    );
  }

  if (status === 'unauthed') {
    return <Navigate to={'/login' + location.search + location.hash} replace />;
  }

  return children;
}

// ─── Root Route ───────────────────────────────────────────────────────────────

function RootRoute() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;
      if (event === 'PASSWORD_RECOVERY') { navigate('/update-password', { replace: true }); return; }
      if (event === 'SIGNED_IN' && session) {
        const hashParams = parseHashParams(window.location.hash);
        const type = hashParams.get('type');
        if (type === 'recovery') return;
        if (type === 'signup' || type === 'email') {
          window.history.replaceState(null, '', window.location.pathname);
          navigate('/welcome', { replace: true });
          return;
        }
      }
      setIsAuthenticated(!!session);
      setIsLoading(false);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (isMounted) {
        const hashParams = parseHashParams(window.location.hash);
        if (hashParams.get('type') === 'recovery') return;
        setIsAuthenticated(!!session);
        setIsLoading(false);
      }
    });
    return () => { isMounted = false; subscription.unsubscribe(); };
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#111111] text-zinc-400 font-medium tracking-tight">
        Loading StudioSign...
      </div>
    );
  }

  return isAuthenticated
    ? <Navigate to={'/admin' + location.search + location.hash} replace />
    : <Navigate to={'/login' + location.search + location.hash} replace />;
}

// ─── App Router ───────────────────────────────────────────────────────────────

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Login isSignUpRoute={true} />} />
        <Route path="/update-password" element={<UpdatePassword />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/display/main" element={<MainDisplay />} />
        <Route path="/display/lobby" element={<LobbyDisplay />} />
        <Route path="/display/notice" element={<NoticeDisplay />} />
        <Route path="/" element={<RootRoute />} />
      </Routes>
    </BrowserRouter>
  );
}